import React from 'react';
import { Link } from 'react-router-dom';
import {
  IconSelfAnchoring,
  IconPerspectiveShifting,
  IconAdaptiveComm,
  IconRelationalInt,
} from './PillarIcons';

/**
 * Shared four-pillar 2×2 grid.
 * Used on the homepage and CFQ consent screen.
 *
 * Each pillar has a custom thin-stroke SVG icon in the brand gold.
 * No coloured left bars — gold/20 left rule only.
 * When compact=false (homepage), each card links to /pillars#<anchor>.
 */

const GOLD = '#C99316';
const IVORY = '#FAF8F3';

const PILLARS = [
  {
    num: '01',
    Icon: IconSelfAnchoring,
    title: 'Self-Anchoring',
    subtitle: 'Internal coherence.',
    body: 'Know who you are before you enter the room. Grounded leadership that does not fragment under cultural pressure.',
    anchor: 'self-anchoring',
  },
  {
    num: '02',
    Icon: IconPerspectiveShifting,
    title: 'Perspective-Shifting',
    subtitle: 'Cultural sight.',
    body: 'Read other logics without losing your own. Understanding the reasoning behind difference.',
    anchor: 'perspective-shifting',
  },
  {
    num: '03',
    Icon: IconAdaptiveComm,
    title: 'Adaptive Communication',
    subtitle: 'Clarity across difference.',
    body: 'Adjust how you communicate without changing what you stand for. Translating intent so meaning lands.',
    anchor: 'adaptive-communication',
  },
  {
    num: '04',
    Icon: IconRelationalInt,
    title: 'Relational Integration',
    subtitle: 'Building together.',
    body: 'Create shared space where different cultural logics operate with dignity. Trust across difference.',
    anchor: 'relational-integration',
  },
];

const FourPillarsGrid = ({ compact = false }) => (
  <div data-testid="four-pillars-grid">
    {!compact && (
      <div className="mb-10">
        <span className="block text-[11px] tracking-[0.3em] uppercase text-[#C99316] font-sans font-medium mb-3">
          Four Pillars
        </span>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#FAF8F3] font-light tracking-tight leading-[1.1]">
          The operating system.
        </h2>
      </div>
    )}
    {compact && (
      <p className="text-[#FAF8F3]/50 text-sm font-sans mb-6">
        20 questions. These four pillars.
      </p>
    )}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {PILLARS.map((p) => {
        const cardStyle = { backgroundColor: '#0D2545', borderLeft: '2px solid rgba(201,162,39,0.2)' };
        const inner = (
          <div className="p-5 sm:p-6">
            <p.Icon />
            <span className="block text-[10px] text-[#FAF8F3]/20 font-sans mt-3 mb-1">{p.num}</span>
            <h3 className="font-serif text-base sm:text-lg text-[#FAF8F3] font-light tracking-tight leading-tight mb-1">
              {p.title}
            </h3>
            <p className="text-[#C99316] text-xs font-sans tracking-wide mb-2">
              {p.subtitle}
            </p>
            <p className="text-[#FAF8F3]/40 text-sm font-sans font-light leading-[1.65]">
              {p.body}
            </p>
            {!compact && (
              <span className="inline-block mt-4 text-[10px] uppercase tracking-[0.18em] font-sans text-[#C99316]/40 group-hover:text-[#C99316]/80 transition-colors duration-200">
                Explore →
              </span>
            )}
          </div>
        );

        return compact ? (
          <div
            key={p.num}
            className="flex rounded"
            style={cardStyle}
            data-testid={`pillar-card-${p.num}`}
          >
            {inner}
          </div>
        ) : (
          <Link
            key={p.num}
            to={`/pillars#${p.anchor}`}
            className="group flex rounded transition-all duration-200"
            style={{ ...cardStyle, textDecoration: 'none' }}
            onMouseEnter={e => { e.currentTarget.style.borderLeftColor = 'rgba(201,162,39,0.55)'; e.currentTarget.style.backgroundColor = '#172D4A'; }}
            onMouseLeave={e => { e.currentTarget.style.borderLeftColor = 'rgba(201,162,39,0.2)'; e.currentTarget.style.backgroundColor = '#0D2545'; }}
            data-testid={`pillar-card-${p.num}`}
          >
            {inner}
          </Link>
        );
      })}
    </div>
  </div>
);

export default FourPillarsGrid;
